import type { SupplyPricePeriod } from '@obraplan/core/application/ports/requisition';
import type { ConsolidatedItem } from '@obraplan/core/domain/takeoff/consolidation';
import {
  materialCodeKey,
  productFactorKey,
  type FactorBasis,
  type MaterialPrice,
  type MaterialPriceOrigin,
  type ProductUnitFactor,
  type SupplyPriceSnapshot,
} from '@obraplan/core/domain/takeoff/material-price';
import { isPurchasable } from '@obraplan/core/domain/takeoff/takeoff-line';
import { formatBusinessDate } from '@/lib/format';
import { precoUnitario } from './formato';

/**
 * O preço do material na obra, como a aba de itens o lê e o grava (Fase 29, etapa 2 —
 * RN-397, RN-398).
 *
 * O que mora aqui é a parte sem React: o mapa de preços por chave da consolidação (D7), a
 * troca de uma linha pela resposta do servidor, os lotes de 500 do "Atualizar preços"
 * (RN-394), os códigos que ele pergunta e as frases que a tela diz da foto (D5). O gancho
 * (`use-precos-da-obra.ts`) só liga isto ao servidor — e o teste prova a regra sem montar
 * tela nenhuma.
 *
 * ## Por que o preço não passa pelo rascunho da bancada
 *
 * A foto do compras é tirada pelo servidor, na hora, e descartar o rascunho por outro
 * motivo não pode levar junto uma foto que já existe. Por isso o mapa daqui é a cópia do
 * que o servidor guardou, e cada gravação troca a linha pela que ele devolveu — e não por
 * um palpite da tela sobre o que ele faria com o pedido.
 */

/** Os preços da obra, pela chave da consolidação: `codigo:…` ou `descricao:…` (D7). */
export type PrecosDaObra = ReadonlyMap<string, MaterialPrice>;

/** O teto da `preco-medio` por pedido (RN-394): a obra com mais códigos manda em lotes. */
export const CODIGOS_POR_LOTE = 500;

/**
 * O que alguém decide num material — o `SetMaterialPrice` sem a obra e a chave.
 *
 * Campo **ausente** fica como está; **`null`** tira; valor grava. É a mesma convenção do
 * contrato, repetida aqui para a tela não ter de importar o esquema do servidor.
 */
export interface DecisaoDePreco {
  readonly unitFactor?: number | null;
  readonly provisionalPrice?: number | null;
  readonly override?: { readonly price: number; readonly reason: string } | null;
}

/**
 * O fator de um **produto**, como o `SetProductUnitFactors` o recebe (decidido em 13/10/2026):
 * vale para todas as obras, pelo código e pela unidade do item. `factor: null` tira.
 */
export interface DecisaoDeFator {
  readonly code: string;
  readonly itemUnit: string;
  readonly supplyUnit?: string;
  readonly factor: number | null;
  readonly basis: FactorBasis;
}

/** Os fatores dos produtos, por `productFactorKey` — de qualquer obra que os confirmou. */
export type FatoresDosProdutos = ReadonlyMap<string, ProductUnitFactor>;

/**
 * O mapa com os fatores que o servidor devolveu para estes códigos — e sem os que ele não
 * devolveu.
 *
 * O servidor responde por código, com todas as unidades: o código perguntado que voltou sem um
 * fator é um fator que alguém tirou, e a tela tem de esquecê-lo. Os outros códigos ficam.
 */
export function comFatores(
  fatores: FatoresDosProdutos,
  codigos: readonly string[],
  achados: readonly ProductUnitFactor[],
): FatoresDosProdutos {
  const perguntados = new Set(codigos.map(materialCodeKey));
  const proximo = new Map([...fatores].filter(([, fator]) => !perguntados.has(fator.codeKey)));
  for (const fator of achados) proximo.set(productFactorKey(fator.code, fator.itemUnit), fator);
  return proximo;
}

/** O fator do produto deste material, quando alguém já o confirmou. */
export function fatorDoProdutoDe(
  fatores: FatoresDosProdutos,
  item: { readonly code?: string; readonly unit?: string },
): ProductUnitFactor | undefined {
  if (item.code === undefined || item.unit === undefined) return undefined;
  if (item.code.trim() === '' || item.unit.trim() === '') return undefined;
  return fatores.get(productFactorKey(item.code, item.unit));
}

/** Os códigos das linhas de preço, uma vez cada — os produtos cujo fator a tela precisa. */
export function codigosDosPrecos(precos: readonly MaterialPrice[]): readonly string[] {
  const porChave = new Map<string, string>();
  for (const preco of precos) {
    if (preco.code === undefined || preco.code.trim() === '') continue;
    const chave = materialCodeKey(preco.code);
    if (!porChave.has(chave)) porChave.set(chave, preco.code.trim());
  }
  return [...porChave.values()];
}

/** A lista do servidor vira o mapa da tela. */
export function mapaDePrecos(precos: readonly MaterialPrice[]): Map<string, MaterialPrice> {
  return new Map(precos.map((preco) => [preco.key, preco]));
}

/**
 * O mapa com a linha que o servidor devolveu — ou sem ela, quando ele a tirou.
 *
 * Ausente é "a linha não guardava mais nada, e saiu" (`SetMaterialPrice`): tirar o
 * provisório de quem só tinha provisório apaga o registro, e a tela tem de esquecê-lo
 * também, em vez de mostrar um provisório que não existe mais.
 */
export function comPreco(
  precos: PrecosDaObra,
  key: string,
  preco: MaterialPrice | undefined,
): PrecosDaObra {
  const proximo = new Map(precos);
  if (preco === undefined) proximo.delete(key);
  else proximo.set(preco.key, preco);
  return proximo;
}

/** O mapa com as linhas que a foto nova trocou — as outras ficam como estavam. */
export function comPrecos(precos: PrecosDaObra, novos: readonly MaterialPrice[]): PrecosDaObra {
  if (novos.length === 0) return precos;
  const proximo = new Map(precos);
  for (const preco of novos) proximo.set(preco.key, preco);
  return proximo;
}

/** A lista em pedaços de até `tamanho` — os lotes do "Atualizar preços" (RN-394). */
export function emLotes<T>(lista: readonly T[], tamanho = CODIGOS_POR_LOTE): readonly T[][] {
  const lotes: T[][] = [];
  for (let inicio = 0; inicio < lista.length; inicio += tamanho) {
    lotes.push(lista.slice(inicio, inicio + tamanho));
  }
  return lotes;
}

/**
 * Os códigos que o "Atualizar preços" pergunta: os dos **materiais** da obra, uma vez cada.
 *
 * A mão de obra fica de fora — não tem código (RN-252), e o preço dela é o R$/h da função
 * (etapa 3). Material sem código também: não há o que perguntar ao compras, e o preço dele
 * é o provisório. O mesmo código em duas grafias é um material só, pela chave da
 * consolidação; a primeira grafia é a que vai.
 */
export function codigosDosMateriais(itens: readonly ConsolidatedItem[]): readonly string[] {
  const porChave = new Map<string, string>();
  for (const item of itens) {
    if (!isPurchasable(item.kind) || item.code === undefined || item.code.trim() === '') continue;
    const chave = materialCodeKey(item.code);
    if (!porChave.has(chave)) porChave.set(chave, item.code.trim());
  }
  return [...porChave.values()];
}

/**
 * Os códigos que ainda não têm foto do compras, e que ninguém tentou buscar nesta sessão.
 *
 * É a D5 depois da importação: a planilha que traz o código de compras **informa** o código, e o
 * preço tem de vir sem que alguém precise lembrar do "Atualizar preços". O código que já foi
 * tentado — com resposta ou com falha — não volta, para a tela não insistir no compras a cada
 * mudança do rascunho.
 */
export function codigosSemFoto(
  codigos: readonly string[],
  precos: PrecosDaObra,
  tentados: ReadonlySet<string>,
): readonly string[] {
  return codigos.filter((codigo) => {
    const chave = materialCodeKey(codigo);
    return precos.get(chave)?.supply === undefined && !tentados.has(chave);
  });
}

/** O que uma rodada do "Atualizar preços" trouxe, contado pelo que o compras disse. */
export interface ResultadoDaAtualizacao {
  /** `false` = não há integração com o compras: nada foi buscado, e não é falha. */
  readonly integrado: boolean;
  readonly comMedia: number;
  readonly semMedia: number;
  readonly naoEncontrados: number;
}

/** Conta as fotos novas pelo estado — o que a frase do resultado diz. */
export function contarFotos(
  precos: readonly MaterialPrice[],
): Omit<ResultadoDaAtualizacao, 'integrado'> {
  let comMedia = 0;
  let semMedia = 0;
  let naoEncontrados = 0;
  for (const preco of precos) {
    const status = preco.supply?.status;
    if (status === 'OK') comMedia += 1;
    else if (status === 'SEM_MEDIA') semMedia += 1;
    else if (status === 'NAO_ENCONTRADO') naoEncontrados += 1;
  }
  return { comMedia, semMedia, naoEncontrados };
}

const quantos = (numero: number, um: string, varios: string): string =>
  `${String(numero)} ${numero === 1 ? um : varios}`;

/**
 * A frase curta do resultado: "38 preços atualizados · 3 sem compra no período · 1 não
 * encontrado".
 *
 * Sem integração é dito como tal, e não como "0 preços": zero acusaria um compras que
 * respondeu sem preço nenhum, e ele nem foi perguntado (D8 — vale o provisório).
 */
export function textoDaAtualizacao(resultado: ResultadoDaAtualizacao): string {
  if (!resultado.integrado) return 'Sem integração com o compras';
  return [
    quantos(resultado.comMedia, 'preço atualizado', 'preços atualizados'),
    resultado.semMedia > 0 ? `${String(resultado.semMedia)} sem compra no período` : undefined,
    resultado.naoEncontrados > 0
      ? quantos(resultado.naoEncontrados, 'não encontrado', 'não encontrados')
      : undefined,
  ]
    .filter((parte) => parte !== undefined)
    .join(' · ');
}

/**
 * O período escolhido serve? As duas pontas `AAAA-MM-DD`, o início antes do fim.
 *
 * O servidor recusa do mesmo jeito; conferir aqui é só não oferecer um botão que vai ser
 * recusado.
 */
export function periodoValido(periodo: SupplyPricePeriod): boolean {
  const data = /^\d{4}-\d{2}-\d{2}$/;
  return data.test(periodo.from) && data.test(periodo.to) && periodo.from <= periodo.to;
}

// ── O que a tela diz ────────────────────────────────────────────────────────────

/** O rótulo curto da origem, no selo ao lado do preço; o longo (`ORIGIN_LABEL`) vai no título. */
export const ROTULO_CURTO_DA_ORIGEM: Readonly<Record<MaterialPriceOrigin, string>> = {
  MEDIO: 'Médio',
  PROVISORIO: 'Provisório',
  DIGITADO: 'Digitado',
};

/** O tom do selo: o médio é o normal; o provisório pede atenção; o digitado é uma decisão. */
export const TOM_DA_ORIGEM: Readonly<Record<MaterialPriceOrigin, 'neutral' | 'warn' | 'info'>> = {
  MEDIO: 'neutral',
  PROVISORIO: 'warn',
  DIGITADO: 'info',
};

/** `R$ 1.234,56` — o **total**, em centavos (D17: as quatro casas são do unitário). */
export function reais(valor: number): string {
  return `R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** O instante da foto no dia de quem olha: `"2026-10-13T12:00:00Z"` vira `"13/10/2026"`. */
export function diaDaFoto(fetchedAt: string): string {
  const instante = new Date(fetchedAt);
  if (Number.isNaN(instante.getTime())) return fetchedAt;
  const dois = (valor: number): string => String(valor).padStart(2, '0');
  return `${dois(instante.getDate())}/${dois(instante.getMonth() + 1)}/${String(instante.getFullYear())}`;
}

/**
 * `1.85` vira `"1,85"` — o fator como a frase "1 UN = 1,85 kg" o escreve.
 *
 * Até seis casas, e sem separador de milhar: o fator da barra de 16 mm é 0,052809 barra por quilo,
 * e dezessete casas na célula não diriam mais nada a quem confere.
 */
export function textoDoFator(fator: number): string {
  return fator.toLocaleString('pt-BR', { maximumFractionDigits: 6, useGrouping: false });
}

/**
 * A foto do compras em linhas, como o diálogo do preço as mostra (D5).
 *
 * Inteira, e não só o número: "R$ 4,12" sem "3 compras e 2 cotações, de 13/10/2025 a
 * 13/10/2026" não diz se é para confiar, e o orçamento é conferido semanas depois de a foto
 * ser tirada. O preço vem **por unidade do compras** — é dele que o fator parte (D6).
 */
export function linhasDaFoto(foto: SupplyPriceSnapshot): readonly string[] {
  const porUnidade = foto.unit === undefined ? '' : `/${foto.unit}`;
  const periodo = `Período: ${formatBusinessDate(foto.periodFrom)} a ${formatBusinessDate(foto.periodTo)} · foto de ${diaDaFoto(foto.fetchedAt)}`;
  if (foto.status === 'NAO_ENCONTRADO') {
    return ['Código não encontrado no compras (ou inativo)', periodo];
  }
  if (foto.status === 'SEM_MEDIA' || foto.average === undefined) {
    return ['Sem compra nem cotação no período', periodo];
  }
  return [
    `Médio: ${precoUnitario(foto.average)}${porUnidade}`,
    `${quantos(foto.purchases ?? 0, 'compra', 'compras')}, ${quantos(foto.quotations ?? 0, 'cotação', 'cotações')}`,
    ...(foto.lowest === undefined || foto.highest === undefined
      ? []
      : [`Menor ${precoUnitario(foto.lowest)} · maior ${precoUnitario(foto.highest)}`]),
    ...(foto.lastPurchaseDate === undefined || foto.lastPurchasePrice === undefined
      ? []
      : [
          `Última compra: ${precoUnitario(foto.lastPurchasePrice)} em ${formatBusinessDate(foto.lastPurchaseDate)}`,
        ]),
    periodo,
  ];
}
